import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { OnboardingService } from '../services/onboarding.service';
import { ReturnUrlService } from '../services/return-url.service';
import { isOrganisationRequiredError } from '../api/api-error';
import { AssetsService } from '../services/assets.service';

export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const onboardingService = inject(OnboardingService);
  const router = inject(Router);
  const returnUrlService = inject(ReturnUrlService);
  const assetsService = inject(AssetsService);
  const currentUrl = state.url || '/';
  const currentPath = onboardingService.normalizePath(currentUrl);
  const isOnboardingRoute = onboardingService.isOnboardingRoute(currentPath);
  const queryReturnUrl = route.queryParamMap.get('returnUrl');

  if (!await authService.isLoggedIn()) {
    returnUrlService.setReturnUrl(currentUrl);
    return router.createUrlTree(['/auth/login'], {
      queryParams: {
        returnUrl: currentUrl
      }
    });
  }

  const bypassOnboardingRoutes = [
    '/employment/respond',
    '/register-invited',
  ];

  if (bypassOnboardingRoutes.some(path => currentPath.startsWith(path))) {
    return true;
  }

  try {
    const onboardingStatus = await onboardingService.getStatus();
    const nextOnboardingRoute = onboardingService.getNextOnboardingRoute(onboardingStatus);

    if (nextOnboardingRoute) {
      if (isOnboardingRoute) {
        if (onboardingService.normalizePath(nextOnboardingRoute) === currentPath) {
          return true;
        }

        return router.createUrlTree([nextOnboardingRoute], {
          queryParams: {
            returnUrl: resolveOnboardingReturnUrl(
              queryReturnUrl,
              returnUrlService,
              onboardingService
            ),
          }
        });
      }

      returnUrlService.setReturnUrl(currentUrl);
      return router.createUrlTree([nextOnboardingRoute], {
        queryParams: {
          returnUrl: currentUrl
        }
      });
    }

    if (isOnboardingRoute) {
      const destination = resolvePostOnboardingRoute(
        queryReturnUrl,
        returnUrlService.consumeReturnUrl(),
        onboardingService
      );

      if (destination !== currentPath) {
        return router.parseUrl(destination);
      }
    }
  } catch (error) {
    if (isOnboardingRoute) {
      return true;
    }

    if (isOrganisationRequiredError(error)) {
      returnUrlService.setReturnUrl(currentUrl);
      return router.createUrlTree(['/onboarding/organisation-setup'], {
        queryParams: {
          returnUrl: currentUrl
        }
      });
    }

    // A temporarily unavailable backend is not evidence that the user has no
    // organisation. Keep the requested route active while the global offline
    // banner reports the connectivity problem and retries in the background.
    return true;
  }

  if (!await assetsService.isRouteEnabled(currentUrl)) {
    return router.createUrlTree(['/home']);
  }

  return true;
};

function resolveOnboardingReturnUrl(
  queryReturnUrl: string | null,
  returnUrlService: ReturnUrlService,
  onboardingService: OnboardingService,
): string {
  const queryValue = sanitizeReturnUrl(queryReturnUrl, onboardingService);
  if (queryValue) {
    return queryValue;
  }

  const storedValue = sanitizeReturnUrl(
    returnUrlService.getReturnUrl(),
    onboardingService
  );
  if (storedValue) {
    return storedValue;
  }

  return '/home';
}

function resolvePostOnboardingRoute(
  queryReturnUrl: string | null,
  storedReturnUrl: string | null,
  onboardingService: OnboardingService,
): string {
  return (
    sanitizeReturnUrl(queryReturnUrl, onboardingService) ||
    sanitizeReturnUrl(storedReturnUrl, onboardingService) ||
    '/home'
  );
}

function sanitizeReturnUrl(
  url: string | null | undefined,
  onboardingService: OnboardingService,
): string | null {
  const value = `${url ?? ''}`.trim();
  if (!value.startsWith('/')) {
    return null;
  }

  const path = onboardingService.normalizePath(value);
  if (onboardingService.isOnboardingRoute(path)) {
    return null;
  }

  return value;
}
